import {useContext, useEffect} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import Modal from 'patient-ping-remedy/packages/modal';
import {DropdownItemResponse, DropdownItemType} from '@/api/dto/dto';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import ValidatedInput from '@common/ValidatedInput';
import Validate from '@/helpers/validate';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';

type Props = {
  isOpen: boolean;
  close(): void;
  dropdownItem?: DropdownItemResponse | null;
}

export default function DropdownItemModal({ isOpen, close, dropdownItem }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: DropdownItemResponse) => dropdownItem
      ? carecoApi?.updateDropdownItem(dropdownItem.id!, payload)
      : carecoApi?.createDropdownItem(payload),
    onSuccess() {
      close();
      void queryClient.invalidateQueries({ queryKey: ['dropdownItems'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: `Dropdown Item ${dropdownItem ? 'updated' : 'created'} successfully.`,
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(
        `Failed to ${dropdownItem ? 'update' : 'create'} Dropdown Item. ${Helpers.traceId(error)}`,
        error,
      );
    }
  });

  const { form, setInitialForm, isValid, isFormChanged, handleChange, handleSubmit } = useForm(() => {
    const payload: DropdownItemResponse = {
      type: form.type.value.value,
      value: DisplayHelpers.removeUnnecessaryWhitespace(form.value.value),
      label: DisplayHelpers.removeUnnecessaryWhitespace(form.label.value),
      active: true,
    };
    mutate(payload);
  });

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        'type',
        {
          value: dropdownItem?.type ?? Object.values(DropdownItemType)[0],
          label: DisplayHelpers.humanize(dropdownItem?.type ?? Object.values(DropdownItemType)[0]),
        },
        { valid: true, message: '' },
        [Validate.required],
        true,
      ),
      ...createFormFieldConfig(
        'value',
        dropdownItem?.value || '',
        { valid: !!dropdownItem, message: '' },
        [Validate.required],
        true,
      ),
      ...createFormFieldConfig(
        'label',
        dropdownItem?.label || '',
        { valid: !!dropdownItem, message: '' },
        [Validate.required],
        true,
      ),
    });
  }, [isOpen, dropdownItem]);

  return (
    <Modal
      isOpen={isOpen}
      hideModal={close}
      headerText={`${dropdownItem ? 'Edit' : 'Create'} Dropdown Item`}
      className={css({ width: '70rem' })}
      buttons={[
        <Button key="cancel" styleType={StyleType.TERTIARY} onClick={close}>Cancel</Button>,
        <Button
          key="create"
          onClick={handleSubmit}
          disabled={!isValid() || isPending || (!!dropdownItem && !isFormChanged())}
        >
          {dropdownItem ? 'Update' : 'Create'}
        </Button>
      ]}
    >
      {!Helpers.isEmptyObject(form) && (
        <>
          <DropdownList
            id="type"
            label="Type"
            required={true}
            value={form.type?.value}
            items={Object.values(DropdownItemType).map((type) => ({
              value: type,
              label: DisplayHelpers.humanize(type),
            }))}
            handleChange={(value : Value | null) => handleChange(null, 'type', value)}
            error={!form.type?.valid.valid}
            errorMessage={form.type?.valid.message}
          />
          <ValidatedInput
            id="value"
            labelText="Value"
            required="required"
            value={form.value?.value || ''}
            onChange={(e) => handleChange(e, 'value', e.target.value)}
            validate={() => form.value?.valid.valid ? '' : form.value?.valid.message}
          />
          <ValidatedInput
            id="label"
            labelText="Label"
            required="required"
            value={form.label?.value || ''}
            onChange={(e) => handleChange(e, 'label', e.target.value)}
            validate={() => form.label?.valid.valid ? '' : form.label?.valid.message}
          />
        </>
      )}
    </Modal>
  );
}
